// TemplatesModal.js
import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback
} from 'react';
import FocusTrap from 'focus-trap-react';
import './TemplatesModal.css';
import PreviewCanvas from '../PreviewCanvas';
import useInView from '../../hooks/useInView';
import { getLibrarySearchScore } from '../../utils/librarySearch';

const levelOptions = ['All', '1', '2', '3'];

const subjectOptions = [
  'All Subjects',
  'Literacy',
  'Maths',
  'Science',
  'Art',
  'Communication'
];

/*
 * Frontend-only subject labels.
 *
 * The keys remain the original stored values so existing templates,
 * filtering and uploaded data are not changed.
 */
const subjectDisplayNames = {
  'All Subjects': 'All Subjects',
  Literacy: 'Literacy & Phonics',
  Maths: 'Maths',
  Science: 'Science & Investigation',
  Art: 'Art & Crafts',
  Communication: 'Communication & Language'
};

const sortOptions = [
  { value: 'random', label: 'Random' },
  { value: 'newest', label: 'Recently Added' },
  { value: 'az', label: 'Alphabetical: A–Z' },
  { value: 'za', label: 'Alphabetical: Z–A' },
  { value: 'oldest', label: 'Oldest Added' }
];

const POPULAR_TAGS_URL =
  'https://edugroove.com/edugroove_backend/templates/PopularTags.json';

/* Used only if the public JSON file cannot be loaded. */
const fallbackPopularTagsConfig = {
  specialOccasions: [
    {
      label: 'Easter',
      uploadTag: 'Easter',
      searchTerms: ['easter'],
      start: '04-01',
      end: '04-30'
    },
    {
      label: 'Halloween',
      uploadTag: 'Halloween',
      searchTerms: ['halloween'],
      start: '10-01',
      end: '10-31'
    },
    {
      label: 'Christmas',
      uploadTag: 'Christmas',
      searchTerms: ['christmas'],
      start: '12-01',
      end: '12-31'
    }
  ],
  popularTags: [
    { label: 'Continuous Provision', uploadTag: 'Continuous Provision', searchTerms: ['continuous provision'] },
    { label: 'Games', uploadTag: 'Game', searchTerms: ['game'] },
    { label: 'Fine Motor Activities', uploadTag: 'Fine Motor', searchTerms: ['fine motor'] },
    { label: 'Flashcards', uploadTag: 'Flashcards', searchTerms: ['flashcard'] },
    { label: 'Cutting Activities', uploadTag: 'Cut', searchTerms: ['cut'] },
    { label: 'Twinkl Phonics', uploadTag: 'Twinkl', searchTerms: ['twinkl'] },
    { label: 'Jolly Phonics', uploadTag: 'Jolly Phonics', searchTerms: ['jolly phonics'] }
  ]
};

function normalisePopularTagOption(option) {
  if (!option || typeof option !== 'object') return null;

  const label = String(option.label || option.uploadTag || option.value || '').trim();
  const uploadTag = String(option.uploadTag || option.value || label).trim();
  const rawSearchTerms = Array.isArray(option.searchTerms)
    ? option.searchTerms
    : [option.searchTerm || uploadTag];
  const searchTerms = rawSearchTerms
    .map(term => String(term).trim().toLowerCase())
    .filter(Boolean);

  if (!label || searchTerms.length === 0) return null;

  return {
    ...option,
    label,
    uploadTag,
    searchTerms
  };
}

function isOccasionCurrentlyVisible(option) {
  const start = String(option.start || '').trim();
  const end = String(option.end || '').trim();

  if (!start || !end) return false;

  const now = new Date();
  const monthDay = `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  return start <= end
    ? monthDay >= start && monthDay <= end
    : monthDay >= start || monthDay <= end;
}

function getTemplateDate(template) {
  if (!template?.createdAt) return 0;

  const timestamp = new Date(template.createdAt).getTime();

  return Number.isNaN(timestamp) ? 0 : timestamp;
}

/*
 * Creates a stable random number for each template.
 *
 * The Random order will remain unchanged while the modal is open,
 * rather than reshuffling every time React updates.
 */
function getStableRandomValue(template, seed) {
  const source =
    `${template?.file || ''}-` +
    `${template?.category || ''}-` +
    `${seed}`;

  let hash = 2166136261;

  for (let i = 0; i < source.length; i += 1) {
    hash ^= source.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function getYouTubeEmbedUrl(url) {
  if (!url) return '';

  try {
    const parsedUrl = new URL(url);
    const host = parsedUrl.hostname.replace(/^www\./, '');
    let videoId = '';

    if (host === 'youtu.be') {
      videoId = parsedUrl.pathname.split('/').filter(Boolean)[0] || '';
    } else if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (parsedUrl.pathname === '/watch') {
        videoId = parsedUrl.searchParams.get('v') || '';
      } else {
        const parts = parsedUrl.pathname.split('/').filter(Boolean);
        if (['shorts', 'embed', 'live'].includes(parts[0])) {
          videoId = parts[1] || '';
        }
      }
    }

    return /^[\w-]{6,}$/.test(videoId)
      ? `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`
      : '';
  } catch (error) {
    return '';
  }
}

const TemplateVideoModal = ({ template, onClose }) => {
  const embedUrl = getYouTubeEmbedUrl(template?.videoUrl);
  const isPortrait = template?.videoAspectRatio === '9:16';

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!embedUrl) return null;

  return (
    <div
      className="template-video-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="template-video-title"
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <FocusTrap>
        <div className={`template-video-modal ${isPortrait ? 'portrait' : 'landscape'}`}>
          <div className="template-video-header">
            <h2 id="template-video-title">
              {template.name || 'Activity tutorial'}
            </h2>
            <button
              type="button"
              className="templates-modal-close-btn template-video-close-btn"
              onClick={onClose}
              aria-label="Close video"
            >
              ×
            </button>
          </div>
          <div className="template-video-frame">
            <iframe
              src={embedUrl}
              title={`${template.name || 'Activity'} tutorial video`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>
      </FocusTrap>
    </div>
  );
};

// --- FREE USER VISIBILITY: helpers ---
function hasTemplateTag(template) {
  if (!template) return false;

  const raw = Array.isArray(template.tags)
    ? template.tags
    : typeof template.tags === 'string'
      ? template.tags.split(',')
      : [];

  const tags = raw.map(tag =>
    String(tag).trim().toLowerCase()
  );

  return (
    tags.includes('template') ||
    tags.includes('group 1')
  );
}

const TemplatesModal = ({
  templates,
  categories,
  isLoadingTemplates,
  searchTerm,
  onClose,
  onLoadTemplate,
  onSearchTermChange,
  onFetchTemplates,
  templatesLoadError
}) => {
  const modalRef = useRef(null);
  const gridRef = useRef(null);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState('All');
  const [selectedSubject, setSelectedSubject] = useState('All Subjects');
  const [selectedPopularTag, setSelectedPopularTag] = useState(null);
  const [popularTagsConfig, setPopularTagsConfig] = useState(
    fallbackPopularTagsConfig
  );

  // Random is the default each time the modal is opened.
  const [selectedSort, setSelectedSort] = useState('random');

  // One stable random arrangement per modal session.
  const [randomSeed, setRandomSeed] = useState(() => Date.now());

  const [visibleCount, setVisibleCount] = useState(24);
  const [videoTemplate, setVideoTemplate] = useState(null);

  useEffect(() => {
    let isCancelled = false;

    fetch(`${POPULAR_TAGS_URL}?v=${Date.now()}`, {
      cache: 'no-store'
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Popular tags request failed: ${response.status}`);
        }

        return response.json();
      })
      .then(config => {
        if (
          !isCancelled &&
          config &&
          (Array.isArray(config.popularTags) ||
            Array.isArray(config.specialOccasions))
        ) {
          setPopularTagsConfig(config);
        }
      })
      .catch(error => {
        console.warn('Using fallback popular tags:', error);
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  const visiblePopularTags = useMemo(() => {
    const specialOccasions = Array.isArray(popularTagsConfig.specialOccasions)
      ? popularTagsConfig.specialOccasions
          .map(normalisePopularTagOption)
          .filter(Boolean)
          .filter(isOccasionCurrentlyVisible)
      : [];

    const popularTags = Array.isArray(popularTagsConfig.popularTags)
      ? popularTagsConfig.popularTags
          .map(normalisePopularTagOption)
          .filter(Boolean)
      : [];

    return [...specialOccasions, ...popularTags];
  }, [popularTagsConfig]);

  const selectedPopularTagOption = useMemo(
    () =>
      visiblePopularTags.find(
        option => option.label === selectedPopularTag
      ) || null,
    [visiblePopularTags, selectedPopularTag]
  );

  useEffect(() => {
    setVisibleCount(24);
  }, [
    searchTerm,
    selectedCategory,
    selectedLevel,
    selectedSubject,
    selectedPopularTag,
    selectedSort,
    randomSeed
  ]);

  // --- FREE USER VISIBILITY: read user + compute isFreeUser ---
  let storedUser = {};

  try {
    storedUser =
      JSON.parse(localStorage.getItem('edugroove_user')) || {};
  } catch (error) {
    storedUser = {};
  }

  // Prefer explicit membership; fallback to roles for safety.
  const isFreeUser =
    storedUser?.membership === 'free' ||
    (
      Array.isArray(storedUser?.roles) &&
      storedUser.roles.includes('armember_basic')
    );

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape' && !videoTemplate) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, videoTemplate]);

  const getTemplateSearchScore = useCallback(
    template => {
      const tags = Array.isArray(template.tags)
        ? template.tags
        : typeof template.tags === 'string'
          ? template.tags.split(',')
          : [];

      return getLibrarySearchScore(searchTerm, [
        {
          value: template.name,
          weight: 1
        },

        ...tags.map(tag => ({
          value: tag,
          weight: 0.85
        })),

        {
          value: template.category,
          weight: 0.55
        },

        {
          value: template.subject,
          weight: 0.5
        },

        {
          value: template.level,
          weight: 0.3
        }
      ]);
    },
    [searchTerm]
  );

  const templateMatchesFilters = useCallback(
    template => {
      const searchScore =
        getTemplateSearchScore(template);

      const templateLevels = String(template.level || '')
        .split(',')
        .map(level => level.trim())
        .filter(Boolean);

      const templateSubjects = String(template.subject || '')
        .split(',')
        .map(subject => subject.trim().toLowerCase())
        .filter(Boolean);

      const matchesLevel =
        selectedLevel === 'All' ||
        templateLevels.includes(selectedLevel);

      const matchesSubject =
        selectedSubject === 'All Subjects' ||
        templateSubjects.includes(
          selectedSubject.toLowerCase()
        );

      const templateTags = (Array.isArray(template.tags)
        ? template.tags
        : typeof template.tags === 'string'
          ? template.tags.split(',')
          : []
      ).map(tag => String(tag).trim().toLowerCase());

      const popularTagSearchTerms = selectedPopularTagOption
        ? selectedPopularTagOption.searchTerms
        : [];

      const templateName =
        String(template.name || '').trim().toLowerCase();

      const matchesPopularTag =
        popularTagSearchTerms.length === 0 ||
        popularTagSearchTerms.some(searchTerm =>
          templateTags.some(tag => tag.includes(searchTerm)) ||
          templateName.includes(searchTerm)
        );

      return (
        searchScore >= 0 &&
        matchesLevel &&
        matchesSubject &&
        matchesPopularTag
      );
    },
    [
      getTemplateSearchScore,
      selectedLevel,
      selectedSubject,
      selectedPopularTagOption
    ]
  );

  // --- FREE USER VISIBILITY ---
  // Build the visible base list before all other filtering and counts.
  const visibilityScopedTemplates = useMemo(() => {
    const list = Array.isArray(templates)
      ? templates
      : [];

    if (!isFreeUser) {
      return list;
    }

    return list.filter(hasTemplateTag);
  }, [templates, isFreeUser]);

  const allFilteredTemplates = useMemo(() => {
    const hasSearch =
      String(searchTerm || '').trim().length > 0;

    return visibilityScopedTemplates
      .map((template, originalIndex) => ({
        template,
        originalIndex,
        score: getTemplateSearchScore(template)
      }))
      .filter(({ template, score }) => {
        const matchesCategory =
          selectedCategory === null ||
          template.category === selectedCategory;

        return (
          score >= 0 &&
          matchesCategory &&
          templateMatchesFilters(template)
        );
      })
      .sort((a, b) => {
        const nameA = String(
          a.template.name || ''
        ).toLowerCase();

        const nameB = String(
          b.template.name || ''
        ).toLowerCase();

        /*
         * Searches continue to prioritise relevance.
         *
         * The selected sort method is then used for templates
         * with the same search score.
         */
        if (hasSearch && b.score !== a.score) {
          return b.score - a.score;
        }

        if (selectedSort === 'newest') {
          const dateDifference =
            getTemplateDate(b.template) -
            getTemplateDate(a.template);

          return (
            dateDifference ||
            nameA.localeCompare(nameB)
          );
        }

        if (selectedSort === 'oldest') {
          const dateA = getTemplateDate(a.template);
          const dateB = getTemplateDate(b.template);

          // Templates with no date are placed at the end.
          if (!dateA && dateB) return 1;
          if (dateA && !dateB) return -1;

          return (
            dateA - dateB ||
            nameA.localeCompare(nameB)
          );
        }

        if (selectedSort === 'az') {
          return nameA.localeCompare(nameB);
        }

        if (selectedSort === 'za') {
          return nameB.localeCompare(nameA);
        }

        if (selectedSort === 'random') {
          return (
            getStableRandomValue(a.template, randomSeed) -
            getStableRandomValue(b.template, randomSeed)
          );
        }

        return a.originalIndex - b.originalIndex;
      })
      .map(({ template }) => template);
  }, [
    visibilityScopedTemplates,
    selectedCategory,
    selectedSort,
    randomSeed,
    templateMatchesFilters,
    getTemplateSearchScore,
    searchTerm
  ]);

  const filteredTemplates = useMemo(
    () => allFilteredTemplates.slice(0, visibleCount),
    [allFilteredTemplates, visibleCount]
  );

  const allCategoriesWithTemplates = useMemo(
    () =>
      categories.filter(category =>
        visibilityScopedTemplates.some(template =>
          template.category === category &&
          templateMatchesFilters(template)
        )
      ),
    [
      categories,
      visibilityScopedTemplates,
      templateMatchesFilters
    ]
  );

  const totalCount = useMemo(
    () =>
      visibilityScopedTemplates.filter(
        templateMatchesFilters
      ).length,
    [
      visibilityScopedTemplates,
      templateMatchesFilters
    ]
  );

  const categoryCounts = useMemo(() => {
    const counts = {};

    categories.forEach(category => {
      const count =
        visibilityScopedTemplates.filter(
          template =>
            template.category === category &&
            templateMatchesFilters(template)
        ).length;

      if (count > 0) {
        counts[category] = count;
      }
    });

    return counts;
  }, [
    categories,
    visibilityScopedTemplates,
    templateMatchesFilters
  ]);

  /*
   * Opens the complete category represented by the selected card.
   *
   * Existing search, level and subject filters are cleared so they
   * cannot make the category appear empty or incomplete.
   */
  const handleViewCategory = useCallback(
    category => {
      if (!category) return;

      setSelectedCategory(category);
      setSelectedLevel('All');
      setSelectedSubject('All Subjects');
      setSelectedPopularTag(null);
      setVisibleCount(24);

      if (String(searchTerm || '').trim()) {
        onSearchTermChange('');
      }

      requestAnimationFrame(() => {
        if (gridRef.current) {
          gridRef.current.scrollTop = 0;
        }
      });
    },
    [
      searchTerm,
      onSearchTermChange
    ]
  );

  const isNewTemplate = createdAt => {
    if (!createdAt) return false;

    const createdDate = new Date(createdAt);
    const now = new Date();

    const diffInDays =
      (now - createdDate) /
      (1000 * 60 * 60 * 24);

    return diffInDays <= 14;
  };

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="templates-modal-title"
    >
      <FocusTrap active={!videoTemplate}>
        <div
          className="templates-modal"
          ref={modalRef}
        >
          <div className="templates-modal-inner">
            <div className="templates-modal-header">
              <h2
                id="templates-modal-title"
                className="templates-modal-title"
              >
                Templates
              </h2>

              <button
                type="button"
                onClick={onClose}
                className="templates-modal-close-btn"
                aria-label="Close Templates Modal"
              >
                ×
              </button>
            </div>

            <div className="templates-modal-controls">
              <input
                type="search"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={event =>
                  onSearchTermChange(event.target.value)
                }
                aria-label="Search templates"
                title={
                  'Search tips: "exact phrase", maths|counting, phonics -ending'
                }
                autoComplete="off"
                spellCheck="false"
              />

              <div className="level-filters">
                {levelOptions.map(level => (
                  <button
                    type="button"
                    key={level}
                    className={
                      `level-button ${
                        selectedLevel === level
                          ? 'active'
                          : ''
                      }`
                    }
                    onClick={() =>
                      setSelectedLevel(level)
                    }
                  >
                    {level === 'All'
                      ? 'All Levels'
                      : level === '1'
                        ? 'Level 1 (EYFS 1)'
                        : level === '2'
                          ? 'Level 2 (EYFS 2)'
                          : 'Level 3 (KS1)'
                    }
                  </button>
                ))}
              </div>

              <select
                value={selectedSubject}
                onChange={event =>
                  setSelectedSubject(event.target.value)
                }
                className="subject-dropdown"
                aria-label="Filter by subject"
              >
                {subjectOptions.map(subject => (
                  <option
                    key={subject}
                    value={subject}
                  >
                    {subjectDisplayNames[subject] || subject}
                  </option>
                ))}
              </select>

              <select
                value={selectedSort}
                onChange={event => {
                  const nextSort = event.target.value;

                  setSelectedSort(nextSort);

                  /*
                   * Returning to Random creates a fresh arrangement.
                   *
                   * The arrangement then remains stable until the
                   * user changes the sorting again or reopens the modal.
                   */
                  if (nextSort === 'random') {
                    setRandomSeed(Date.now());
                  }
                }}
                className="sort-dropdown"
                aria-label="Sort templates"
              >
                {sortOptions.map(option => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    Sort: {option.label}
                  </option>
                ))}
              </select>

              <div className="popular-tags-row">
                <span className="popular-tags-label">Popular:</span>

                <div className="popular-tags-list">
                  {visiblePopularTags.map(tag => (
                    <button
                      type="button"
                      key={tag.label}
                      className={
                        `popular-tag-button ${
                          selectedPopularTag === tag.label
                            ? 'active'
                            : ''
                        }`
                      }
                      onClick={() =>
                        setSelectedPopularTag(currentTag =>
                          currentTag === tag.label ? null : tag.label
                        )
                      }
                      aria-pressed={selectedPopularTag === tag.label}
                    >
                      {tag.label}
                    </button>
                  ))}
                </div>

                <div
                  className="templates-results-count"
                  aria-live="polite"
                >
                  {allFilteredTemplates.length === 1
                    ? '1 template found'
                    : `${allFilteredTemplates.length} templates found`
                  }
                </div>
              </div>
            </div>

            <div className="templates-modal-content">
              <div className="templates-modal-sidebar">
                <ul>
                  <li>
                    <button
                      type="button"
                      className={
                        `category-button ${
                          selectedCategory === null
                            ? 'active'
                            : ''
                        }`
                      }
                      onClick={() =>
                        setSelectedCategory(null)
                      }
                      aria-label="Select All Templates"
                    >
                      All Templates ({totalCount})
                    </button>
                  </li>

                  {allCategoriesWithTemplates.map(
                    category => (
                      <li key={category}>
                        <button
                          type="button"
                          className={
                            `category-button ${
                              selectedCategory === category
                                ? 'active'
                                : ''
                            }`
                          }
                          onClick={() =>
                            setSelectedCategory(category)
                          }
                          aria-label={
                            `Select category ${category.replace(
                              /_/g,
                              ' '
                            )}`
                          }
                        >
                          {category.replace(/_/g, ' ')}
                          {' '}
                          ({categoryCounts[category] || 0})
                        </button>
                      </li>
                    )
                  )}
                </ul>
              </div>

              <div
                className="templates-modal-grid"
                ref={gridRef}
              >
                {filteredTemplates.length > 0 ? (
                  filteredTemplates.map(
                    template => (
                      <LazyTemplateCard
                        key={`${template.file}-${template.category}`}
                        template={template}
                        onLoadTemplate={onLoadTemplate}
                        onViewCategory={handleViewCategory}
                        isNewTemplate={isNewTemplate}
                        showViewCategory={selectedCategory === null}
                        onLearn={setVideoTemplate}
                      />
                    )
                  )
                ) : isLoadingTemplates ? (
                  <p className="templates-loading-text">
                    Loading templates...
                  </p>
                ) : templatesLoadError ? (
                  <div className="templates-loading-error" role="alert">
                    <p>{templatesLoadError}</p>
                    <button
                      type="button"
                      className="show-more-button"
                      onClick={() => onFetchTemplates('All Worksheets')}
                    >
                      Try Again
                    </button>
                  </div>
                ) : (
                  <p>No templates match your filters.</p>
                )}

                {filteredTemplates.length > 0 && templatesLoadError && (
                  <div className="templates-loading-error" role="status">
                    <p>{templatesLoadError}</p>
                    <button
                      type="button"
                      className="show-more-button"
                      onClick={() => onFetchTemplates('All Worksheets')}
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {filteredTemplates.length <
                  allFilteredTemplates.length && (
                  <div className="template-card show-more-card">
                    <button
                      type="button"
                      onClick={() =>
                        setVisibleCount(
                          currentCount =>
                            currentCount + 24
                        )
                      }
                      className="show-more-button"
                    >
                      Show More
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </FocusTrap>
      {videoTemplate && (
        <TemplateVideoModal
          template={videoTemplate}
          onClose={() => setVideoTemplate(null)}
        />
      )}
    </div>
  );
};

const LazyTemplateCard = ({
  template,
  onLoadTemplate,
  onViewCategory,
  isNewTemplate,
  showViewCategory,
  onLearn
}) => {
  const [hideHoverActions, setHideHoverActions] = useState(false);
  const [ref, isInView] = useInView({
    threshold: 0.1
  });

  const [firstPage, setFirstPage] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    setFirstPage(null);
    setHasFetched(false);
  }, [
    template.file,
    template.category
  ]);

  useEffect(() => {
    if (!hasFetched && isInView) {
      setHasFetched(true);

      const templatePath =
        `https://edugroove.com/edugroove_backend/templates/` +
        `${template.category}/${template.file}?v=${Date.now()}`;

      fetch(templatePath, {
        cache: 'no-store'
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(
              `Template request failed: ${response.status}`
            );
          }

          return response.json();
        })
        .then(fileJson => {
          setFirstPage(
            fileJson.pages?.[0] || null
          );
        })
        .catch(error => {
          console.warn(
            `⚠️ Failed lazy load: ${template.name}`,
            error
          );

          setHasFetched(false);
        });
    }
  }, [
    isInView,
    hasFetched,
    template.file,
    template.category,
    template.name
  ]);

  const tagListRaw = Array.isArray(template.tags)
    ? template.tags
    : typeof template.tags === 'string'
      ? template.tags.split(',')
      : [];

  const tagList = tagListRaw
    .map(tag =>
      String(tag).trim().toLowerCase()
    )
    .filter(Boolean);

  const hasTwinklTag =
    tagList.includes('twinkl');

  const hasJollyTag =
    tagList.includes('jolly phonics');

  const categoryName = String(
    template.category || ''
  ).replace(/_/g, ' ');

  const openTemplate = () => {
    onLoadTemplate(
      template.file,
      template.category,
      template
    );
  };

  return (
    <div
      ref={ref}
      className={
        `template-card ${
          isInView ? 'fade-in' : 'fade-out'
        } ${
          hideHoverActions ? 'hide-hover-actions' : ''
        }`
      }
      onMouseLeave={() => setHideHoverActions(false)}
      style={{
        backgroundColor:
          template.color || '#f0f0f0'
      }}
      onClick={openTemplate}
      role="button"
      tabIndex={0}
      aria-label={
        `Load template ${template.name}`
      }
      onKeyDown={event => {
        if (
          event.key === 'Enter' ||
          event.key === ' '
        ) {
          event.preventDefault();
          openTemplate();
        }
      }}
    >
      {(hasTwinklTag || hasJollyTag) && (
        <div className="template-phonics-badges">
          {hasTwinklTag && (
            <span
              className={
                'template-phonics-badge twinkl-badge'
              }
              title="Twinkl"
            >
              T
            </span>
          )}

          {hasJollyTag && (
            <span
              className={
                'template-phonics-badge jolly-badge'
              }
              title="Jolly Phonics"
            >
              JP
            </span>
          )}
        </div>
      )}

      {firstPage ? (
        <div className="preview-container">
          <div className="preview-padding-wrapper">
            <PreviewCanvas
              page={firstPage}
              maxWidth={180}
              maxHeight={280}
              className="fade-in"
            />
          </div>
        </div>
      ) : (
        <div className="template-image placeholder">
          Loading preview…
        </div>
      )}

      <div className="template-name-with-badge">
        <p className="template-name">
          {template.name}
        </p>

        {isNewTemplate(template.createdAt) && (
          <span className="new-badge">
            NEW
          </span>
        )}
      </div>

      <div className="template-card-hover-panel">
        <div className="template-card-hover-buttons">
          <button
            type="button"
            className={
              'template-hover-button template-hover-open'
            }
            onClick={event => {
              event.stopPropagation();
              openTemplate();
            }}
          >
            Open Template
          </button>

          {template.videoUrl && getYouTubeEmbedUrl(template.videoUrl) && (
            <button
              type="button"
              className="template-hover-button template-hover-learn"
              onClick={event => {
                event.stopPropagation();
                onLearn(template);
              }}
            >
              Learn
            </button>
          )}

          {showViewCategory && (
            <button
              type="button"
              className={
                'template-hover-button template-hover-category'
              }
              onClick={event => {
                event.stopPropagation();
                event.currentTarget.blur();

                setHideHoverActions(true);
                onViewCategory(template.category);
              }}
              title={
                `View all templates in ${categoryName}`
              }
            >
              View Category
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplatesModal;
